'use client'

import { type ComponentProps, type ReactNode, useMemo, useRef, useState } from 'react'

import Filter from '../../../icons/Filter'
import Timelock from '../../../icons/Timelock'
import { cn } from '../../../utils/cn'
import { Text, type TextProps } from '../../typography/Text'
import { Button, type ButtonProps } from '../Button'
import { Checkbox } from '../Checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '../Command'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover'
import { CategoryControls } from './CategoryControls'
import { MultiSelectClearButton } from './MultiSelectClearButton'

type PopoverContentProps = ComponentProps<typeof PopoverContent>

type MultiSelectOption = {
  value: string
  component: ReactNode
  keywords?: string[]
  category?: string | null
}

interface MultiSelectProps {
  label: string
  options: MultiSelectOption[]
  values: string[]
  onChange: (values: string[]) => void
  formatCategoryLabel?: (categoryId: string) => string
  align?: PopoverContentProps['align']
  side?: PopoverContentProps['side']
  sideOffset?: PopoverContentProps['sideOffset']
  triggerVariant?: ButtonProps['variant']
  triggerSize?: ButtonProps['size']
  triggerClassName?: string
  triggerIcon?: ReactNode
  showSelectedCount?: boolean
  /** Text size token for the trigger label. Defaults to `'3xs'`. */
  labelSize?: TextProps['size']
  /**
   * Render options as labeled sections grouped by their `category` — one
   * `CommandGroup` per category, headed via `formatCategoryLabel`, shown
   * alongside the category pills. Opt-in (default `false`) so existing
   * consumers keep the flat list + pills behavior unchanged.
   */
  groupByCategory?: boolean
  /**
   * Lock the control: the trigger is non-interactive (the popover can't open)
   * and renders a lock icon. Use when the current selection is forced by an
   * external constraint so the values still read as an active filter.
   */
  disabled?: boolean
  /** Hover/focus tooltip on the trigger, e.g. to explain why it is `disabled`. */
  tooltip?: ButtonProps['tooltip']
  /**
   * Show an inline clear button when one or more values are selected. Set to
   * `false` when clearing all values would be unsafe.
   */
  showClearButton?: boolean
  /**
   * Hide the popover's search row for short, fixed option sets (e.g. column
   * pickers) where filtering is noise. Also hides the row's inline Clear.
   */
  showSearch?: boolean
}

export function MultiSelect(props: MultiSelectProps) {
  const {
    label,
    options,
    values,
    onChange,
    formatCategoryLabel,
    align = 'start',
    side,
    sideOffset,
    triggerVariant = 'ghost',
    triggerSize = 'sm',
    triggerClassName,
    triggerIcon,
    showSelectedCount = true,
    labelSize = '3xs',
    groupByCategory = false,
    disabled = false,
    tooltip,
    showClearButton = true,
    showSearch = true
  } = props
  const selectedCount = values.length
  const resolvedTriggerIcon =
    triggerIcon ?? (disabled ? <Timelock className="size-5" /> : <Filter className="size-5" />)
  const selectedCountLabel = showSelectedCount && selectedCount > 0 ? ` (${selectedCount})` : ''
  // A string `tooltip` backfills the trigger's `aria-label` in `Button`, which
  // would otherwise replace the filter's name with the lock reason. Keep the
  // label as the accessible name so locked filters stay distinguishable.
  const triggerAriaLabel =
    typeof tooltip === 'string' ? `${label}. ${tooltip}` : `${label}${selectedCountLabel}`
  const listRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [sortingCategory, setSortingCategory] = useState<string | null>(null)

  const sortedOptions = useMemo(() => {
    if (!sortingCategory) return options

    const categoryOptions: MultiSelectOption[] = []
    const otherOptions: MultiSelectOption[] = []

    for (const option of options) {
      if (option.category === sortingCategory) {
        categoryOptions.push(option)
      } else {
        otherOptions.push(option)
      }
    }

    return [...categoryOptions, ...otherOptions]
  }, [options, sortingCategory])

  function clearSelection() {
    onChange([])
    setSortingCategory(null)
  }

  const hasCategories = useMemo(() => {
    return options.some(opt => opt.category != null)
  }, [options])

  function toggleValue(optionValue: string) {
    const newValues = values.includes(optionValue)
      ? values.filter(v => v !== optionValue)
      : [...values, optionValue]

    onChange(newValues)
  }

  const groupedOptions = useMemo(() => {
    if (!groupByCategory) return []

    // Map iteration order is insertion order, so groups come out in first-seen order.
    const byCategory = new Map<string | null, MultiSelectOption[]>()
    for (const option of options) {
      const category = option.category ?? null
      const existing = byCategory.get(category)
      if (existing) {
        existing.push(option)
      } else {
        byCategory.set(category, [option])
      }
    }
    return [...byCategory].map(([category, categoryOptions]) => ({
      category,
      options: categoryOptions
    }))
  }, [groupByCategory, options])

  const renderOption = (option: MultiSelectOption) => (
    <CommandItem
      key={option.value}
      value={option.value}
      keywords={option.keywords}
      onSelect={() => {
        toggleValue(option.value)
      }}
    >
      <Checkbox checked={values.includes(option.value)} />
      {option.component}
    </CommandItem>
  )

  return (
    <Popover open={disabled ? false : open} onOpenChange={next => setOpen(disabled ? false : next)}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          aria-expanded={open}
          aria-disabled={disabled || undefined}
          aria-label={triggerAriaLabel}
          tooltip={tooltip}
          variant={triggerVariant}
          size={triggerSize}
          className={cn(
            'text-secondary-foreground',
            disabled && 'cursor-not-allowed opacity-60',
            triggerClassName
          )}
        >
          {resolvedTriggerIcon}
          <Text size={labelSize} className="gap-2xs flex items-center">
            {label}
            {showSelectedCount && selectedCount > 0 && (
              <span className="tabular-nums">({selectedCount})</span>
            )}
          </Text>
          {showClearButton && !disabled && selectedCount > 0 && (
            <MultiSelectClearButton label={label} onClear={clearSelection} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align={align} side={side} sideOffset={sideOffset}>
        <div className="relative">
          <Command>
            {showSearch && (
              <div className="flex h-9 items-center border-b *:data-[slot=command-input-wrapper]:flex-1">
                <CommandInput
                  placeholder={`Search for ${label.toLowerCase()}`}
                  className="text-xs"
                  onValueChange={() => {
                    if (listRef.current) listRef.current.scrollTop = 0
                  }}
                />
                {!hasCategories && (
                  <Button
                    disabled={selectedCount === 0}
                    onClick={clearSelection}
                    variant="ghost"
                    size="sm"
                    className="mr-2 ml-auto shrink-0"
                  >
                    Clear
                  </Button>
                )}
              </div>
            )}
            {hasCategories && (
              <div className="flex h-9 w-full items-center overflow-x-auto border-b px-2">
                <CategoryControls
                  options={options}
                  values={values}
                  formatCategoryLabel={formatCategoryLabel}
                  onSelect={onChange}
                  onSortingCategoryChange={setSortingCategory}
                />
              </div>
            )}
            <CommandList ref={listRef}>
              <CommandEmpty>{`No ${label.toLowerCase()}s found.`}</CommandEmpty>
              {groupByCategory ? (
                groupedOptions.map(group => (
                  <CommandGroup
                    key={group.category ?? '__ungrouped'}
                    className="[&_[cmdk-group-heading]]:text-3xs"
                    heading={
                      group.category != null
                        ? (formatCategoryLabel?.(group.category) ?? group.category)
                        : undefined
                    }
                  >
                    {group.options.map(renderOption)}
                  </CommandGroup>
                ))
              ) : (
                <CommandGroup>{sortedOptions.map(renderOption)}</CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export type { MultiSelectOption }
